import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UUID } from "../../../utils/uuid";
import toast from "react-hot-toast";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import type {
  CustomDashboardConfig,
  EditableCustomDashboardConfig,
} from "../../luzmo/custom-dashboard-config";
import { toCustomDashboardConfig } from "../../luzmo/custom-dashboard-config";
import { CeAdminSupabaseAccountService } from "../../generic/server/supabase-account-service";

interface AccountCustomDashboardsState {
  dashboards: CustomDashboardConfig[];
  loading: boolean;
  error: Error | null;
  save: (config: EditableCustomDashboardConfig) => Promise<boolean>;
  remove: (dashboardId: UUID) => Promise<void>;
}

export const useAccountCustomDashboards = (accountId: UUID): AccountCustomDashboardsState => {
  const pendo = usePendo();
  const {
    data: loadedDashboards,
    loading,
    error: loadError,
    reload,
  } = useSupabaseLoader(
    async ({ supabase }) => {
      const { data: rows, error: queryError } = await new CeAdminSupabaseAccountService(
        supabase,
      ).getCustomLuzmoDashboards(accountId);
      if (queryError) throw new Error("Could not load custom Luzmo dashboards");
      return rows.map(toCustomDashboardConfig);
    },
    [accountId],
    "ce_admin",
  );

  const persist = useSupabaseCallback(
    async ({ supabase }, config: EditableCustomDashboardConfig) => {
      const { error: saveError } = await new CeAdminSupabaseAccountService(
        supabase,
      ).upsertCustomLuzmoDashboard(accountId, config);
      if (saveError) throw saveError;
      reload();
    },
    [accountId, reload],
    "ce_admin",
  );

  const destroy = useSupabaseCallback(
    async ({ supabase }, dashboardId: UUID) => {
      const { error: deleteError } = await new CeAdminSupabaseAccountService(
        supabase,
      ).deleteCustomLuzmoDashboard(accountId, dashboardId);
      if (deleteError) throw deleteError;
      reload();
    },
    [accountId, reload],
    "ce_admin",
  );

  const save = async (config: EditableCustomDashboardConfig): Promise<boolean> => {
    const previous = loadedDashboards?.find(({ id }) => id === config.id);
    try {
      await persist(config);
      if (config.enabled !== (previous?.enabled ?? false)) {
        pendo?.track("ce-admin-custom-luzmo-dashboard-toggled", {
          dashboardId: config.id,
          enabled: config.enabled,
        });
      }
      toast.success("Saved custom dashboard settings");
      return true;
    } catch {
      toast.error("Could not save custom dashboard settings");
      return false;
    }
  };

  const remove = async (dashboardId: UUID): Promise<void> => {
    const previous = loadedDashboards?.find(({ id }) => id === dashboardId);
    try {
      await destroy(dashboardId);
      if (previous?.enabled) {
        pendo?.track("ce-admin-custom-luzmo-dashboard-toggled", {
          dashboardId,
          enabled: false,
        });
      }
      toast.success("Deleted custom dashboard");
    } catch {
      toast.error("Could not delete custom dashboard");
    }
  };

  return {
    dashboards: loadedDashboards ?? [],
    loading,
    error: loadError,
    save,
    remove,
  };
};
